import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowDownTrayIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline'

export const RECOVERY_FILE_NAME = 'vitahub-recovery-codes.txt'

/**
 * VH-285 — the recovery codes, shown once (FinanceHub FH-104).
 *
 * The server keeps only their hashes, so this screen is the only time they
 * exist. The way on stays closed until the reader says they are stored: a
 * habitual click on "continue" would lose them for good.
 */
export default function RecoveryCodes({ codes, onDone, doneLabel }) {
  const { t } = useTranslation()
  const [stored, setStored] = useState(false)
  const [copied, setCopied] = useState(null)

  const asText = codes.join('\n')

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText)
      setCopied('done')
    } catch {
      setCopied('failed')
    }
  }

  const download = () => {
    const url = URL.createObjectURL(new Blob([`${asText}\n`], { type: 'text/plain' }))
    const link = document.createElement('a')
    link.href = url
    link.download = RECOVERY_FILE_NAME
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <section aria-labelledby="recovery-codes-title" className="space-y-3">
      <h2 id="recovery-codes-title" className="text-base font-semibold text-gray-900">
        {t('secondFactor.recovery.title')}
      </h2>
      <p className="text-sm text-gray-700">{t('secondFactor.recovery.intro')}</p>
      <ul
        aria-label={t('secondFactor.recovery.listLabel')}
        className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md border border-gray-200 bg-gray-50 p-3 font-mono text-sm text-gray-900"
      >
        {codes.map((code) => (
          <li key={code} className="select-all break-all">
            {code}
          </li>
        ))}
      </ul>
      <div className="flex flex-col gap-2 sm:flex-row">
        <button type="button" onClick={copy} className="btn-secondary inline-flex items-center justify-center gap-1">
          <ClipboardDocumentIcon className="h-4 w-4" />
          {t('secondFactor.recovery.copy')}
        </button>
        <button type="button" onClick={download} className="btn-secondary inline-flex items-center justify-center gap-1">
          <ArrowDownTrayIcon className="h-4 w-4" />
          {t('secondFactor.recovery.download')}
        </button>
      </div>
      <p role="status" className="min-h-5 text-xs text-gray-600">
        {copied === 'done' && t('secondFactor.recovery.copied')}
        {copied === 'failed' && t('secondFactor.recovery.copyFailed')}
      </p>
      <p className="rounded-md bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
        {t('secondFactor.recovery.onceWarning')}
      </p>
      <label className="flex items-start gap-2 text-sm text-gray-900">
        <input
          type="checkbox"
          checked={stored}
          onChange={(event) => setStored(event.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300"
        />
        {t('secondFactor.recovery.stored')}
      </label>
      <button type="button" onClick={onDone} disabled={!stored} className="btn-primary w-full disabled:opacity-50">
        {doneLabel || t('secondFactor.recovery.continue')}
      </button>
    </section>
  )
}
